// DecisionIssueDrawer.jsx — v1.0.8 Document 4 Step 5
//
// Slide-out drawer that opens from the right side when an admin taps any
// issue row in DecisionIntelligenceGroup.jsx (panels 2 or 3).
//
// Wires to:
//   GET  /api/admin/decision-issues/:id
//   POST /api/admin/decision-issues/:id/resolve
//
// Five alert states drive five different action button layouts. Sonner
// toast confirms every success. Defer requires a non-empty reason.
//
// Uses the existing @/components/ui/sheet primitive — identical pattern to
// SchedulerHelpSheet.jsx so it inherits a11y, focus trap, and escape-key
// dismiss for free.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  FileText,
  Zap,
  ShieldAlert,
  FileWarning,
  ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same 5-state → dot color map used in DecisionIntelligenceGroup.
const ALERT_STYLE = {
  "AUTO-FIX FAILED": { bg: "bg-red-500", text: "text-red-700", pulse: true, icon: Zap },
  "CODE FIX REVIEW REQUIRED": { bg: "bg-red-500", text: "text-red-700", pulse: false, icon: AlertCircle },
  "SPEC REVIEW REQUIRED": { bg: "bg-red-500", text: "text-red-700", pulse: false, icon: FileWarning },
  "ARCHITECTURE REVIEW REQUIRED": { bg: "bg-red-500", text: "text-red-700", pulse: false, icon: ShieldAlert },
  "AUTO-FIXED": { bg: "bg-emerald-400", text: "text-emerald-700", pulse: false, icon: CheckCircle2 },
};


export default function DecisionIssueDrawer({ issueId, open, onOpenChange, onResolved }) {
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(null); // which action is in-flight
  const [deferReason, setDeferReason] = useState("");

  // Fetch the full issue any time the drawer opens with a new id.
  useEffect(() => {
    if (!open || !issueId) return;
    let cancelled = false;
    setLoading(true);
    setIssue(null);
    setDeferReason("");
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/decision-issues/${issueId}`);
        if (!cancelled) setIssue(res.data);
      } catch (e) {
        if (!cancelled) {
          toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
          onOpenChange?.(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, issueId, onOpenChange]);

  const doResolve = useCallback(
    async (action, successMessage, reasonOverride = null) => {
      if (!issue) return;
      // defer requires a reason
      const finalReason = reasonOverride ?? (action === "defer" ? deferReason.trim() : "");
      if (action === "defer" && !finalReason) {
        toast.error("Defer requires a reason");
        return;
      }
      setResolving(action);
      try {
        const res = await axios.post(
          `${API}/admin/decision-issues/${issue.issue_id}/resolve`,
          { action, reason: finalReason || undefined },
        );
        toast.success(successMessage, {
          description: res.data?.issue?.resolution
            ? `Resolution: ${res.data.issue.resolution}`
            : undefined,
          duration: 4000,
        });
        onResolved?.(res.data.issue);
        onOpenChange?.(false);
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message;
        toast.error(`Resolve failed: ${detail}`);
      } finally {
        setResolving(null);
      }
    },
    [issue, deferReason, onOpenChange, onResolved],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid="decision-issue-drawer"
      >
        {loading && (
          <div className="flex items-center gap-2 py-10 justify-center text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading issue…
          </div>
        )}
        {!loading && issue && (
          <>
            <DrawerHeader issue={issue} />
            <SectionA issue={issue} />
            <SectionB
              issue={issue}
              resolving={resolving}
              deferReason={deferReason}
              setDeferReason={setDeferReason}
              onResolve={doResolve}
            />
            <DrawerFooter issue={issue} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}


// ─── Header ────────────────────────────────────────────────────

function DrawerHeader({ issue }) {
  const style = ALERT_STYLE[issue.alert_state] || ALERT_STYLE["AUTO-FIXED"];
  const Icon = style.icon;
  const shortTrace = (issue.trace_id || "").slice(0, 8);
  return (
    <SheetHeader className="pb-4 border-b border-slate-100">
      <SheetTitle className="text-base font-bold text-slate-800 flex items-start gap-3">
        <span className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Icon className={`w-4 h-4 ${style.text}`} />
        </span>
        <span className="flex-1">
          <span className="block" data-testid="drawer-title">
            {issue.criterion_failed || "(no criterion)"}
          </span>
          <span className="block text-xs font-normal text-slate-400 mt-0.5">
            <code className="font-mono">{shortTrace}…</code> · {fmtTime(issue.created_at)}
          </span>
        </span>
      </SheetTitle>
      <div className="flex items-center gap-2 mt-2">
        <span
          className={`w-2.5 h-2.5 rounded-full ${style.bg} ${style.pulse ? "animate-pulse" : ""}`}
          data-testid="drawer-status-dot"
        />
        <span className={`text-xs font-bold ${style.text}`} data-testid="drawer-alert-state">
          {issue.alert_state}
        </span>
        {issue.sla_exceeded && (
          <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 uppercase tracking-wide">
            SLA Exceeded
          </span>
        )}
      </div>
    </SheetHeader>
  );
}


// ─── Section A — What Happened ─────────────────────────────────

function SectionA({ issue }) {
  return (
    <section className="mt-5 space-y-3" data-testid="drawer-section-a">
      <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
        What Happened
      </h3>
      <KVRow label="Failure Type" value={pretty(issue.failure_classification)} testId="drawer-failure-type" />
      <KVRow label="Mode Used" value={issue.mode_used || "—"} testId="drawer-mode-used" />
      <KVRow
        label="Tools Used"
        value={issue.tools_used?.length ? issue.tools_used.join(", ") : "—"}
        testId="drawer-tools-used"
      />
      <KVRow
        label="Confidence"
        value={
          issue.confidence != null
            ? `${Number(issue.confidence).toFixed(2)} (${confidenceTier(issue.confidence)})`
            : "—"
        }
        testId="drawer-confidence"
      />
      <KVRow label="File Reference" value={issue.file_reference || "—"} mono testId="drawer-file-reference" />
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">
          Expected Output
        </p>
        <pre
          className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-emerald-900 whitespace-pre-wrap font-mono"
          data-testid="drawer-expected-output"
        >
          {issue.expected_output || "(none)"}
        </pre>
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">
          Actual Output
        </p>
        <pre
          className="text-xs bg-red-50 border border-red-200 rounded-lg p-2.5 text-red-900 whitespace-pre-wrap font-mono"
          data-testid="drawer-actual-output"
        >
          {issue.actual_output || "(none)"}
        </pre>
      </div>
      {issue.reasoning?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">
            Reasoning
          </p>
          <ul className="text-xs text-slate-700 space-y-1 list-disc list-inside" data-testid="drawer-reasoning">
            {issue.reasoning.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}


// ─── Section B — What To Do (action buttons vary by state) ─────

function SectionB({ issue, resolving, deferReason, setDeferReason, onResolve }) {
  return (
    <section className="mt-5 space-y-3" data-testid="drawer-section-b">
      <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
        What To Do
      </h3>
      {issue.recommended_action && (
        <div className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid="drawer-recommended-action">
          {issue.recommended_action}
        </div>
      )}

      {issue.status === "resolved" ? (
        <div className="text-xs text-emerald-700 bg-emerald-50 rounded-lg p-3 border border-emerald-200">
          <p className="font-bold">
            <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> Resolved
          </p>
          <p className="mt-1">
            By <strong>{issue.resolved_by || "(unknown)"}</strong> at{" "}
            {fmtTime(issue.resolved_at)}
          </p>
          {issue.resolution && <p className="mt-1 italic">{issue.resolution}</p>}
        </div>
      ) : (
        <ActionButtons
          issue={issue}
          resolving={resolving}
          deferReason={deferReason}
          setDeferReason={setDeferReason}
          onResolve={onResolve}
        />
      )}
    </section>
  );
}

function ActionButtons({ issue, resolving, deferReason, setDeferReason, onResolve }) {
  const state = issue.alert_state;
  const busy = !!resolving;

  // CODE FIX REVIEW REQUIRED: Approve Fix / Reject / View Proposed Patch
  if (state === "CODE FIX REVIEW REQUIRED") {
    return (
      <div className="grid grid-cols-2 gap-2">
        <Btn
          testId="drawer-action-approve-fix"
          disabled={busy}
          onClick={() => onResolve("approve_fix", "Patch approved — retest queued")}
          variant="primary"
          loading={resolving === "approve_fix"}
        >
          Approve Fix
        </Btn>
        <Btn
          testId="drawer-action-reject"
          disabled={busy}
          onClick={() => onResolve("reject", "Marked for manual fix")}
          variant="danger"
          loading={resolving === "reject"}
        >
          Reject
        </Btn>
        {issue.autofix_patch_proposed && (
          <button
            className="col-span-2 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-semibold text-slate-700 transition"
            onClick={() => viewPatch(issue.autofix_patch_proposed)}
            data-testid="drawer-action-view-patch"
          >
            <FileText className="w-3.5 h-3.5" /> View Proposed Patch
          </button>
        )}
      </div>
    );
  }

  // SPEC REVIEW REQUIRED: Open Spec Document / Mark Clarified / Defer (reason)
  if (state === "SPEC REVIEW REQUIRED") {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Btn
            testId="drawer-action-mark-clarified"
            disabled={busy}
            onClick={() => onResolve("mark_clarified", "Spec clarified — fix queued")}
            variant="primary"
            loading={resolving === "mark_clarified"}
          >
            Mark Clarified
          </Btn>
          <Btn
            testId="drawer-action-open-spec"
            disabled={busy}
            onClick={() => openSpecDoc(issue)}
            variant="ghost"
          >
            Open Spec Document
          </Btn>
        </div>
        <DeferBlock
          deferReason={deferReason}
          setDeferReason={setDeferReason}
          busy={busy}
          loading={resolving === "defer"}
          onDefer={() => onResolve("defer", "Deferred to backlog")}
        />
      </div>
    );
  }

  // ARCHITECTURE REVIEW REQUIRED: Escalate / View Arch Docs / Defer (reason)
  if (state === "ARCHITECTURE REVIEW REQUIRED") {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Btn
            testId="drawer-action-escalate"
            disabled={busy}
            onClick={() => onResolve("escalate", "Escalated to architect")}
            variant="primary"
            loading={resolving === "escalate"}
          >
            Escalate to Architect
          </Btn>
          <Btn
            testId="drawer-action-view-arch-docs"
            disabled={busy}
            onClick={() => openArchDocs()}
            variant="ghost"
          >
            View Arch Docs
          </Btn>
        </div>
        <DeferBlock
          deferReason={deferReason}
          setDeferReason={setDeferReason}
          busy={busy}
          loading={resolving === "defer"}
          onDefer={() => onResolve("defer", "Deferred to backlog")}
        />
      </div>
    );
  }

  // AUTO-FIX FAILED: View Failure Details / Assign Manual Fix
  if (state === "AUTO-FIX FAILED") {
    return (
      <div className="grid grid-cols-2 gap-2">
        <Btn
          testId="drawer-action-assign-manual"
          disabled={busy}
          onClick={() => onResolve("assign_manual", "Assigned for manual fix")}
          variant="primary"
          loading={resolving === "assign_manual"}
        >
          Assign Manual Fix
        </Btn>
        <button
          className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-semibold text-slate-700 transition disabled:opacity-40"
          disabled={busy}
          onClick={() => viewPatch(issue.autofix_patch_proposed || "(no patch on file)")}
          data-testid="drawer-action-view-failure"
        >
          <FileText className="w-3.5 h-3.5" /> View Failure Details
        </button>
      </div>
    );
  }

  // AUTO-FIXED: Dismiss (also view details, but that's just reading the drawer)
  if (state === "AUTO-FIXED") {
    return (
      <div className="grid grid-cols-1 gap-2">
        <Btn
          testId="drawer-action-dismiss"
          disabled={busy}
          onClick={() => onResolve("dismiss", "Dismissed from active issues")}
          variant="ghost"
          loading={resolving === "dismiss"}
        >
          Dismiss
        </Btn>
      </div>
    );
  }

  return null;
}

function DeferBlock({ deferReason, setDeferReason, busy, loading, onDefer }) {
  return (
    <div className="rounded-lg border border-slate-200 p-2.5 bg-slate-50">
      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
        Or Defer — reason required
      </label>
      <textarea
        value={deferReason}
        onChange={(e) => setDeferReason(e.target.value)}
        placeholder="Why is this being deferred? (required)"
        rows={2}
        className="w-full text-xs p-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-300"
        disabled={busy}
        data-testid="drawer-defer-reason"
      />
      <Btn
        testId="drawer-action-defer"
        disabled={busy || !deferReason.trim()}
        onClick={onDefer}
        variant="amber"
        loading={loading}
        className="mt-2 w-full"
      >
        Defer to Backlog
      </Btn>
    </div>
  );
}


// ─── Footer ────────────────────────────────────────────────────

function DrawerFooter({ issue }) {
  return (
    <footer
      className="mt-5 pt-4 border-t border-slate-100 text-xs text-slate-400 space-y-1.5"
      data-testid="drawer-footer"
    >
      <p>
        <strong className="text-slate-600">Build Issues Register entry:</strong>{" "}
        <code className="font-mono text-slate-500">{issue.issue_id}</code>
      </p>
      {issue.trace_id && (
        <button
          className="inline-flex items-center gap-1.5 hover:text-slate-700 transition"
          onClick={() => openDecisionLogForTrace(issue.trace_id)}
          data-testid="drawer-open-log-link"
        >
          <ExternalLink className="w-3 h-3" />
          View full decision log entry
        </button>
      )}
    </footer>
  );
}


// ─── Primitives / helpers ──────────────────────────────────────

function KVRow({ label, value, testId, mono = false }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-28 text-[11px] font-bold uppercase tracking-wide text-slate-400 flex-shrink-0">
        {label}
      </span>
      <span
        className={`text-xs text-slate-700 ${mono ? "font-mono" : ""} flex-1 break-words`}
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}

function Btn({ children, variant = "primary", loading, testId, className = "", ...rest }) {
  const base =
    "inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed";
  const v = {
    primary: "bg-slate-900 hover:bg-slate-800 text-white",
    danger: "bg-red-600 hover:bg-red-700 text-white",
    amber: "bg-amber-500 hover:bg-amber-600 text-white",
    ghost: "border border-slate-200 hover:bg-slate-50 text-slate-700",
  }[variant];
  return (
    <button data-testid={testId} className={`${base} ${v} ${className}`} {...rest}>
      {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      {children}
    </button>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function pretty(s) {
  return (s || "").replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "—";
}

function confidenceTier(c) {
  if (c == null) return "unknown";
  if (c >= 0.85) return "high";
  if (c >= 0.6) return "medium";
  return "low";
}

function viewPatch(patch) {
  const w = window.open("", "_blank");
  if (!w) {
    toast.error("Popup blocked — allow popups");
    return;
  }
  w.document.title = "Proposed Patch";
  w.document.body.innerHTML = `
    <pre style="font-family:ui-monospace,Menlo,monospace;font-size:12px;padding:16px;background:#0f172a;color:#e2e8f0;min-height:100vh;white-space:pre-wrap;margin:0;">${escapeHtml(
      patch || "(no patch recorded)",
    )}</pre>`;
}

function openSpecDoc(issue) {
  // Spec lives in /app/memory/BUILD_ACCEPTANCE_MATRIX.md. In the admin pod we don't expose that directly,
  // so surface the criterion ref in a toast and point the founder to the docs in the codebase.
  toast.info("Spec lives in BUILD_ACCEPTANCE_MATRIX.md", {
    description: `Criterion: ${issue.criterion_failed || "(unknown)"}`,
    duration: 6000,
  });
}

function openArchDocs() {
  toast.info("Architecture docs", {
    description: "See /app/memory/ORCHESTRATOR_FINAL.md + DECISION_ENGINE_FINAL.md",
    duration: 6000,
  });
}

function openDecisionLogForTrace(traceId) {
  const token = localStorage.getItem("token");
  fetch(`${API}/admin/decision-log?limit=200`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((r) => r.json())
    .then((data) => {
      const matching = (data.items || []).filter((d) => d.trace_id === traceId);
      const w = window.open("", "_blank");
      if (!w) return;
      w.document.title = `Decision log — ${traceId.slice(0, 8)}`;
      w.document.body.innerHTML = `
        <pre style="font-family:ui-monospace,Menlo,monospace;font-size:12px;padding:16px;background:#0f172a;color:#e2e8f0;min-height:100vh;white-space:pre-wrap;margin:0;">${escapeHtml(
          JSON.stringify(
            matching.length ? matching : { note: "No decision_log record yet for this trace_id. Orchestrator has not written to the log yet.", trace_id: traceId },
            null,
            2,
          ),
        )}</pre>`;
    })
    .catch((e) => toast.error(`Log fetch failed: ${e.message}`));
}

function escapeHtml(s) {
  return String(s).replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}
